/**
 * ProofUploadModal Component
 * Combined modal for uploading, viewing, and managing proof-of-discharge documents.
 * Supports drag-and-drop, inline preview, lightbox expansion, and replace/delete.
 *
 * Modes:
 *  - CHOOSE:        Existing proof found on server → prompts user to use it or upload new
 *  - EMPTY:         No proof, no file selected → shows upload drop zone
 *  - FILE_SELECTED: File selected, not yet uploaded → shows local preview + Upload
 *  - VIEWING:       Proof exists on server → shows inline preview + Download/Replace/Delete
 *  - REPLACING:     Proof exists, replacement file selected → shows both + Replace File
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import {
  CloudArrowUpIcon,
  TrashIcon,
  DocumentTextIcon,
  PhotoIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  MagnifyingGlassPlusIcon,
  PaperClipIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathSpinner, OathAlert } from '../../../shared/OathUI';
import { toast } from 'react-toastify';
import { uploadProof, deleteProof, getProofs, downloadProof } from '../../../../services/oathDischargeService';
import { PROOF_FILE_CONFIG } from '../utils/constants';
import { formatCurrencyWithSymbol, formatFileSize } from '../utils/formatters';

// ─── Helpers ────────────────────────────────────────────────────────────────

const getFileExtension = (name) => name?.split('.').pop()?.toLowerCase();

const isImageType = (ct) => ct?.startsWith('image/') && !ct?.includes('tiff');
const isPdfType = (ct) => ct?.startsWith('application/pdf');

const FileIcon = ({ fileName, className = 'h-5 w-5' }) => {
  const ext = getFileExtension(fileName);
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) {
    return <PhotoIcon className={`${className} text-blue-500`} />;
  }
  return <DocumentTextIcon className={`${className} text-red-500`} />;
};

// ─── Component ──────────────────────────────────────────────────────────────

const ProofUploadModal = ({ isOpen, onClose, power, existingFileId = null, onProofUpdated }) => {
  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Auto-fetch state (History tab — no existingFileId prop)
  const [fetchedFileId, setFetchedFileId] = useState(null);
  const [fetchedFileName, setFetchedFileName] = useState(null);
  const [loadingProofs, setLoadingProofs] = useState(false);

  // Choose prompt: shown when existing proof found via auto-fetch (not via explicit prop)
  const [showChoosePrompt, setShowChoosePrompt] = useState(false);

  // Blob preview state (merged from ProofViewerModal)
  const [blobLoading, setBlobLoading] = useState(false);
  const [objectUrl, setObjectUrl] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Derived
  const effectiveFileId = existingFileId || fetchedFileId;
  const hasExisting = !!effectiveFileId;
  const mode = showChoosePrompt
    ? 'choose'
    : hasExisting
      ? (selectedFile ? 'replacing' : 'viewing')
      : (selectedFile ? 'fileSelected' : 'empty');

  const isPdf = isPdfType(contentType);
  const isImage = isImageType(contentType);
  const displayFileName = effectiveFileId || 'Proof Document';

  // ─── Effects ────────────────────────────────────────────────────────────

  // Auto-fetch proof list when opened without existingFileId
  useEffect(() => {
    if (!isOpen || existingFileId || !power?.powerNo) {
      if (!isOpen) {
        setFetchedFileId(null);
        setFetchedFileName(null);
        setSelectedFile(null);
        setError(null);
        setObjectUrl(null);
        setContentType(null);
        setLocalPreviewUrl(null);
        setLightboxOpen(false);
        setBlobLoading(false);
        setShowChoosePrompt(false);
      }
      return;
    }
    let cancelled = false;
    const fetchProofs = async () => {
      setLoadingProofs(true);
      const result = await getProofs(power.powerNo);
      if (cancelled) return;
      if (result.success && result.data?.length > 0) {
        const first = result.data[0];
        setFetchedFileId(first.blobName || first.fileId);
        setFetchedFileName(first.fileName || first.blobName || first.fileId);
        // Prompt: use existing document or upload a new one?
        setShowChoosePrompt(true);
      }
      setLoadingProofs(false);
    };
    fetchProofs();
    return () => { cancelled = true; };
  }, [isOpen, existingFileId, power?.powerNo]);

  // Fetch blob when proof exists (for inline preview)
  useEffect(() => {
    if (!isOpen || !effectiveFileId || !power?.powerNo) return;

    let revoked = false;
    setBlobLoading(true);
    setObjectUrl(null);
    setContentType(null);

    downloadProof(power.powerNo, effectiveFileId).then((result) => {
      if (revoked) return;
      if (result.success && result.data) {
        const url = URL.createObjectURL(result.data.blob);
        setObjectUrl(url);
        setContentType(result.data.contentType);
      }
      setBlobLoading(false);
    });

    return () => { revoked = true; };
  }, [isOpen, effectiveFileId, power?.powerNo]);

  // Revoke object URLs on change / unmount
  useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  // Lightbox ESC key handler
  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setLightboxOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [lightboxOpen]);

  // ─── File Selection ─────────────────────────────────────────────────────

  const validateFile = useCallback((file) => {
    if (!PROOF_FILE_CONFIG.ACCEPTED_TYPES.includes(file.type)) {
      return `"${file.name}" is not an accepted file type. Accepted: PDF, JPG, PNG, GIF, TIFF.`;
    }
    if (file.size > PROOF_FILE_CONFIG.MAX_SIZE_BYTES) {
      return `"${file.name}" exceeds the ${PROOF_FILE_CONFIG.MAX_SIZE_MB}MB file size limit.`;
    }
    return null;
  }, []);

  const selectFile = useCallback((files) => {
    setError(null);
    const file = files[0];
    if (!file) return;
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSelectedFile(file);
    // Local preview for images
    if (file.type.startsWith('image/')) {
      setLocalPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
    } else {
      setLocalPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, [validateFile]);

  const removeFile = useCallback(() => {
    setSelectedFile(null);
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  // ─── Drag & Drop ───────────────────────────────────────────────────────

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragOver(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragOver(false); }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    selectFile(Array.from(e.dataTransfer.files));
  }, [selectFile]);

  const handleFileSelect = useCallback((e) => {
    selectFile(Array.from(e.target.files));
    e.target.value = '';
  }, [selectFile]);

  // ─── Actions ────────────────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);

    try {
      // If replacing an existing file, delete it first
      if (hasExisting) {
        const delResult = await deleteProof(power.powerNo, effectiveFileId);
        if (!delResult.success) {
          setError(delResult.error || 'Failed to replace existing file');
          setUploading(false);
          return;
        }
        onProofUpdated(power.powerNo, effectiveFileId, null, true);
      }

      const result = await uploadProof(power.powerNo, selectedFile);
      if (result.success && result.data) {
        onProofUpdated(power.powerNo, result.data.fileId, result.data.fileName);
        setSelectedFile(null);
        setLocalPreviewUrl(null);
        toast.success('File uploaded successfully');
        onClose();
      } else {
        setError(result.error || `Failed to upload "${selectedFile.name}"`);
      }
    } catch {
      setError('An unexpected error occurred during upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    const result = await deleteProof(power.powerNo, effectiveFileId);
    if (result.success) {
      toast.success('File deleted');
      setFetchedFileId(null);
      setObjectUrl(null);
      setContentType(null);
      onProofUpdated(power.powerNo, effectiveFileId, null, true);
    } else {
      toast.error(result.error || 'Failed to delete file');
    }
    setDeleting(false);
  };

  const handleDownload = useCallback(() => {
    if (!objectUrl) return;
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = displayFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('File downloaded');
  }, [objectUrl, displayFileName]);

  const handleReplace = () => {
    fileInputRef.current?.click();
  };

  // Choose prompt: use the existing document as proof for this discharge
  const handleUseExisting = useCallback(() => {
    onProofUpdated(power.powerNo, fetchedFileId, fetchedFileName);
    toast.success('Existing proof document selected');
    onClose();
  }, [power?.powerNo, fetchedFileId, fetchedFileName, onProofUpdated, onClose]);

  // Choose prompt: skip existing and upload a new document
  const handleUploadNew = useCallback(() => {
    setShowChoosePrompt(false);
    // Clear the fetched file so we go to 'empty' mode
    setFetchedFileId(null);
    setFetchedFileName(null);
    setObjectUrl(null);
    setContentType(null);
  }, []);

  // ─── Modal Title & Footer ──────────────────────────────────────────────

  const modalTitle = mode === 'choose'
    ? 'Existing Proof Found'
    : mode === 'viewing'
      ? 'Proof of Discharge'
      : mode === 'replacing'
        ? 'Replace Proof Document'
        : 'Upload Proof of Discharge';

  const isBusy = uploading || deleting;

  const renderFooter = () => {
    if (mode === 'choose') {
      return (
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 w-full">
          <OathButton variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Cancel
          </OathButton>
          <OathButton variant="outline" onClick={handleUploadNew} className="w-full sm:w-auto">
            Upload New
          </OathButton>
          <OathButton onClick={handleUseExisting} className="w-full sm:w-auto">
            <CheckCircleIcon className="h-4 w-4 mr-1.5" />
            Use Existing
          </OathButton>
        </div>
      );
    }

    if (mode === 'viewing') {
      return (
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full">
          <OathButton variant="outline" size="sm" icon={ArrowDownTrayIcon} onClick={handleDownload} disabled={!objectUrl || blobLoading} className="w-full sm:w-auto">
            Download
          </OathButton>
          <span className="hidden sm:block flex-1" />
          <OathButton variant="outline" size="sm" onClick={handleDelete} disabled={deleting} className="w-full sm:w-auto text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20">
            {deleting ? <><OathSpinner size="sm" /> Deleting...</> : 'Delete'}
          </OathButton>
          <OathButton variant="outline" size="sm" icon={ArrowPathIcon} onClick={handleReplace} className="w-full sm:w-auto">
            Replace
          </OathButton>
          <OathButton variant="outline" size="sm" onClick={onClose} className="w-full sm:w-auto">
            Close
          </OathButton>
        </div>
      );
    }

    if (mode === 'replacing') {
      return (
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 w-full">
          <OathButton variant="outline" onClick={() => removeFile()} disabled={uploading} className="w-full sm:w-auto">
            Cancel
          </OathButton>
          <OathButton onClick={handleUpload} disabled={uploading} className="w-full sm:w-auto">
            {uploading ? <><OathSpinner size="sm" /> Replacing...</> : 'Replace File'}
          </OathButton>
        </div>
      );
    }

    // EMPTY or FILE_SELECTED
    return (
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 w-full">
        <OathButton variant="outline" onClick={onClose} disabled={uploading} className="w-full sm:w-auto">
          Cancel
        </OathButton>
        <OathButton onClick={handleUpload} disabled={!selectedFile || uploading} className="w-full sm:w-auto">
          {uploading ? <><OathSpinner size="sm" /> Uploading...</> : 'Upload'}
        </OathButton>
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  if (!power) return null;

  return (
    <>
      <OathModal
        isOpen={isOpen}
        onClose={isBusy ? undefined : onClose}
        title={modalTitle}
        size="lg"
        footer={renderFooter()}
      >
        <div className="space-y-5">
          {/* Power Info Header */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-mono font-bold text-primary dark:text-primary-light text-sm qhd:text-base">
                {power.powerNo}
              </span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {formatCurrencyWithSymbol(power.bondAmount)}
              </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">{power.defendantName}</p>
            {power.proofReason && (
              <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 font-medium">
                {power.proofReason}
              </p>
            )}
          </div>

          {/* Loading proofs (History tab auto-fetch) */}
          {loadingProofs && (
            <div className="flex items-center justify-center gap-2 py-4">
              <OathSpinner size="sm" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Loading proof...</span>
            </div>
          )}

          {/* ── CHOOSE: Existing proof found — prompt user ─────────────── */}
          {mode === 'choose' && !loadingProofs && (
            <div className="space-y-4">
              <OathAlert variant="primary">
                <p className="text-sm">
                  This power already has a proof document on file. You can use the existing document for this discharge or upload a new one.
                </p>
              </OathAlert>

              {/* Existing document preview */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                {blobLoading ? (
                  <div className="flex items-center justify-center py-10 bg-gray-50 dark:bg-gray-800">
                    <OathSpinner size="sm" />
                    <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading preview...</span>
                  </div>
                ) : objectUrl && isImage ? (
                  <img
                    src={objectUrl}
                    alt="Existing proof document"
                    className="max-w-full max-h-48 object-contain mx-auto block bg-gray-50 dark:bg-gray-800"
                  />
                ) : objectUrl && isPdf ? (
                  <div className="py-8 bg-gray-50 dark:bg-gray-800 text-center">
                    <DocumentTextIcon className="h-10 w-10 text-red-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">PDF Document</p>
                  </div>
                ) : (
                  <div className="py-8 bg-gray-50 dark:bg-gray-800 text-center">
                    <DocumentTextIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Proof Document</p>
                  </div>
                )}
                <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                  <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600 dark:text-gray-400 truncate">{fetchedFileName || 'Proof Document'}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── VIEWING / REPLACING: Inline Preview ────────────────────── */}
          {hasExisting && !showChoosePrompt && !loadingProofs && (
            <div className="space-y-3">
              {blobLoading ? (
                <div className="flex items-center justify-center py-12 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800">
                  <OathSpinner size="md" />
                  <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">Loading preview...</span>
                </div>
              ) : objectUrl && isImage ? (
                /* Image preview — clickable for lightbox */
                <div
                  className="relative border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-gray-50 dark:bg-gray-800 cursor-pointer group"
                  onClick={() => setLightboxOpen(true)}
                >
                  <img
                    src={objectUrl}
                    alt="Proof document"
                    className="max-w-full max-h-64 object-contain mx-auto block"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 active:opacity-100 transition-opacity bg-black/20 dark:bg-black/40">
                    <MagnifyingGlassPlusIcon className="h-8 w-8 text-white drop-shadow-lg" />
                  </div>
                </div>
              ) : objectUrl && isPdf ? (
                /* PDF card — clickable for lightbox */
                <div
                  className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-8 bg-gray-50 dark:bg-gray-800 cursor-pointer text-center group hover:border-primary/50 dark:hover:border-primary-light/50 transition-colors"
                  onClick={() => setLightboxOpen(true)}
                >
                  <DocumentTextIcon className="h-12 w-12 text-red-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{displayFileName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Click to view full size</p>
                </div>
              ) : objectUrl ? (
                /* Non-previewable (TIFF, etc.) */
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-8 bg-gray-50 dark:bg-gray-800 text-center">
                  <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{displayFileName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Preview not available for this file type</p>
                </div>
              ) : (
                /* Fetch failed */
                <OathAlert variant="warning">
                  <p className="text-sm">Could not load preview. You can still download, replace, or delete the file.</p>
                </OathAlert>
              )}

              {/* File metadata */}
              {objectUrl && (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <PaperClipIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{displayFileName}</span>
                </div>
              )}
            </div>
          )}

          {/* ── REPLACING: Replacement File ────────────────────────────── */}
          {mode === 'replacing' && selectedFile && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Replacement File
              </h4>
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 min-w-0">
                  {localPreviewUrl ? (
                    <img src={localPreviewUrl} alt="" className="h-8 w-8 object-cover rounded flex-shrink-0" />
                  ) : (
                    <FileIcon fileName={selectedFile.name} className="h-5 w-5 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
                <button
                  onClick={removeFile}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── EMPTY / FILE_SELECTED: Drop Zone ──────────────────────── */}
          {!hasExisting && !selectedFile && !loadingProofs && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl p-4 sm:p-8 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-primary bg-primary/5 dark:bg-primary/10'
                  : 'border-gray-300 dark:border-gray-600 hover:border-primary/60 dark:hover:border-primary-light/60'
              }`}
            >
              <CloudArrowUpIcon className="h-8 w-8 sm:h-10 sm:w-10 qhd:h-12 qhd:w-12 mx-auto text-gray-400 dark:text-gray-500 mb-2 sm:mb-3" />
              <p className="text-sm qhd:text-base font-medium text-gray-700 dark:text-gray-300">
                Drag a file here or click to browse
              </p>
              <p className="text-xs qhd:text-sm text-gray-500 dark:text-gray-400 mt-1">
                PDF, JPG, PNG, GIF, TIFF — Max {PROOF_FILE_CONFIG.MAX_SIZE_MB}MB
              </p>
            </div>
          )}

          {/* FILE_SELECTED: Selected file preview */}
          {!hasExisting && selectedFile && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Selected File</h4>
              {/* Local image preview */}
              {localPreviewUrl && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-gray-50 dark:bg-gray-800 mb-3">
                  <img
                    src={localPreviewUrl}
                    alt="Selected file preview"
                    className="max-w-full max-h-48 object-contain mx-auto block"
                  />
                </div>
              )}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-2 min-w-0">
                  <FileIcon fileName={selectedFile.name} />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 dark:text-white truncate">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
                <button
                  onClick={removeFile}
                  className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <OathAlert variant="error">
              <p className="text-sm">{error}</p>
            </OathAlert>
          )}
        </div>

        {/* Hidden file input (shared across all modes) */}
        <input
          ref={fileInputRef}
          type="file"
          accept={PROOF_FILE_CONFIG.ACCEPTED_EXTENSIONS}
          onChange={handleFileSelect}
          className="hidden"
        />
      </OathModal>

      {/* ── Lightbox Overlay ───────────────────────────────────────────── */}
      {lightboxOpen && objectUrl && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex flex-col"
          onClick={(e) => { if (e.target === e.currentTarget) setLightboxOpen(false); }}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-2 py-2 sm:px-4 sm:py-3 flex-shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <PaperClipIcon className="h-4 w-4 text-white/70 flex-shrink-0" />
              <span className="text-white/90 text-sm truncate max-w-[120px] sm:max-w-[150px]">{displayFileName}</span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={handleDownload}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                title="Download"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setLightboxOpen(false)}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                title="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div
            className="flex-1 flex items-center justify-center p-2 sm:p-4 overflow-hidden"
            onClick={() => setLightboxOpen(false)}
          >
            {isPdf ? (
              <iframe
                src={objectUrl}
                title="Proof document"
                className="w-full h-full rounded-lg bg-white"
                onClick={(e) => e.stopPropagation()}
              />
            ) : isImage ? (
              <img
                src={objectUrl}
                alt="Proof document"
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="text-center text-white" onClick={(e) => e.stopPropagation()}>
                <DocumentTextIcon className="h-16 w-16 mx-auto mb-4 opacity-60" />
                <p className="text-lg mb-2">{displayFileName}</p>
                <p className="text-sm text-white/60 mb-4">This file type cannot be previewed in the browser.</p>
                <OathButton onClick={handleDownload} icon={ArrowDownTrayIcon}>
                  Download File
                </OathButton>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProofUploadModal;
